import React, { useEffect, useState, useContext } from "react"
import moment from "moment"
import Timer from "react-compound-timer"
import { isMobile, isMobilyOnly } from 'react-device-detect'

import { SEARCH, SearchContext } from "./SearchContext"
import SearchDestination from "./fields/SearchDestination"
import SearchCalendar from './fields/SearchCalendar'
import SearchAdults from './fields/SearchAdults'
import SearchRooms from './fields/SearchRooms'
import SearchKids from './fields/SearchKids'
import RoomsDataPanel from './panels/RoomsDataPanel'
import AdvancedSearchPanel from "./panels/AdvancedSearchPanel"
import FilterResults from "./filtering/FilterResults"

import { getHotels, preparePayloadForGetHotels } from "../helpers/hotelHelper"
import { fetchZohoLead } from "../api/zohoLead"
import { fetchCurrentUser, shouldShowAdvanced } from "../helpers/userHelper"
import { popStateListener } from "../helpers/historyHelper"

const defaultErrors = {
  children: false,
  adults: false,
  kids: false,
  rooms: false
}

const SearchBar = () => {
  const { state, dispatch } = useContext(SearchContext)
  const _setNewState = (payload) => dispatch({ type: SEARCH.ALL, payload })
  const _setSearchBy = (payload) =>
    dispatch({ type: SEARCH.SEARCH_BY.SET, payload })
  const _setHotels   = (payload) => dispatch({ type: SEARCH.HOTELS.SET, payload })
  const _setAvailabilityNotices = (availabilityNotices) => dispatch({ type: SEARCH.AVAILABILITY_NOTICES.SET, payload: availabilityNotices })
  const _setUser     = (payload) => dispatch({ type: SEARCH.USER.SET, payload })
  const _setAdults   = (payload) => dispatch({ type: SEARCH.ADULTS.SET, payload })
  const _setZohoLead = (payload) => dispatch({ type: SEARCH.ZOHO_LEAD.SET, payload })
  const _setFiltersOpen = (open) => dispatch({ type: SEARCH.FILTERS.OPEN, payload: open})

  const [valid, setValid]                   = useState(false)
  const [errors, setErrors]                 = useState(defaultErrors)
  const [mobileOpen, setMobileOpen]         = useState(true)
  const [advancedOpen, setAdvancedOpen]     = useState(false)
  const [roomsOpen, setRoomsOpen]           = useState(false)
  const [isBookingAgent, setIsBookingAgent] = useState(false)
  const [departure, setDeparture]           = useState(moment().add(1, "days"))
  const [maxAdults, setMaxAdults]           = useState(10)
  const [maxKids, setMaxKids]               = useState(10)
  const [moveToNextField, setMoveToNextField] = useState(false)

  useEffect(() => {
    popStateListener(popStateActions)
    __getUser()
    window.history.pushState(null, "Default Search Page", "/search")
  }, [])

  useEffect(() => {
    let validity = true, newErrors = {...errors}
    if(state.searchBy.id == null) {
      validity = false
    }
    // check if 10 adults / room
    if(state.adults > 10 && (state.adults / state.rooms) > 10 ) {
      validity = false
    }
    newErrors.adults = (state.adults > 10 && (state.adults / state.rooms) > 10)

    if (state.rooms > 1) {
      if (state.roomsData.reduce((sum, room) => sum + room.adults, 0) != state.adults) {
        validity = false
      }
      if (state.roomsData.reduce((sum, room) => sum + room.kids, 0) != state.children) {
        validity = false
      }
    }

    if (state.searchBy.country && state.searchBy.country !== 'US' && state.children > 0) {
      setAdvancedOpen(true)
      if (state.rooms === Object.keys(state.childrenAges).length) {
        if (state.rooms === 1) {
          validity = state.childrenAges[1].every(age => age && age > 0)
        } else {
          validity = state.roomsData.every(roomData => {
            return state.childrenAges[roomData.id].every(age => age && age > 0)
          })
        }
      } else {
        validity = false
      }
    }

    setErrors(newErrors)
    setValid(validity)
  }, [state.searchBy.id, state.searchBy.country, state.adults, state.children, state.rooms, state.roomsData, state.childrenAges])

  useEffect(() => {
    setRoomsOpen(state.rooms > 1)
  }, [state.rooms])

  useEffect(() => {
    // Set the payload for childrenAges based on the parameters of rooms and kids

    if (state.rooms === 1) {
      let newChildrenAges = {}
      newChildrenAges[1] =  Array.from({ length: state.children }, (_, i) => null)
      if (state.childrenAges[1]) {
       newChildrenAges[1] = newChildrenAges[1].map((age, idx) => state.childrenAges[1][idx] || null)
      }
      dispatch({ type: SEARCH.CHILDREN_AGES.SET, payload: newChildrenAges })
    } else {
      let newChildrenAges = {}
      state.roomsData.forEach((roomData) => {
        newChildrenAges[roomData.id] = Array.from({ length: roomData.kids }, (_, i) => null)
        if (state.childrenAges[roomData.id]) {
          newChildrenAges[roomData.id] = newChildrenAges[roomData.id].map((age, idx) => state.childrenAges[roomData.id][idx] || null)
        }
      })
      dispatch({ type: SEARCH.CHILDREN_AGES.SET, payload: newChildrenAges })
    }
  }, [state.rooms, state.roomsData, state.children])

  useEffect(() => {
    if(state.initiateFilterSearch.value) {
      fetchHotelRequest(state.searchBy, true)
    }
  }, [state.initiateFilterSearch])

  // History listener actions
  const popStateActions = (location, stateObj) => {
    if (stateObj && "all" in stateObj) {
      let newState = { ...state }
      stateObj.all.isLoading = false
      newState = Object.assign({}, newState, stateObj.all)
      newState.arrival = moment(stateObj.all.arrival)
      if (stateObj.all.searchedArrival) {
        newState.searchedArrival = moment(stateObj.all.searchedArrival)
      }
      newState.searchedArrival = moment

      if (stateObj.useExistingHotels) {
        let existingHotelData = JSON.parse(
          sessionStorage.getItem("hotelsHistory")
        )
        let hotels = {
          currentHotels: existingHotelData.hotels,
          visibleHotels: existingHotelData.hotels,
          hotels: existingHotelData.hotels
        }
        newState = { ...newState, ...hotels }
      }

      _setNewState(newState)

      if (stateObj.resource) {
        setTimeout(() => {
          fetchHotelRequest(stateObj.resource, false, newState)
        }, 300)
      }
    }
  }

  const __getUser = async () => {
    const data = await fetchCurrentUser()
    if (data) {
      _setUser(data)
      setIsBookingAgent(data.role == 'booking_agent')
    }
  }

  const __getZohoLead = async () => {
    try {
      const response = await fetchZohoLead(state.leadId)
      if (!response.data.lead.length_of_stay || (response.data.lead.length_of_stay === 'Other')) {
        alert(`The lead has an invalid length of stay. Please assign and search again`)
      } else {
        _setZohoLead(response.data.lead)
      }
    } catch (error) {
      alert(`Lead againt ${state.leadId} not found`)
    }
  }

  const _submitSearch = (e) => {
    if (e.type === "searchReload") {
      state.searchReloading = true
    } else {
      state.searchReloading = false
    }

    e.preventDefault()

    setRoomsOpen(false)
    setAdvancedOpen(false)

    // reset filters
    dispatch({ type: SEARCH.FILTERS.SEARCH, payload: false })
    dispatch({ type: SEARCH.FILTERS.SET, payload: state.defaultFilterValues })
    dispatch({ type: SEARCH.FILTERS.LAST, payload: {} })
    dispatch({ type: SEARCH.SORT.SET, payload: "preferred" })
    fetchHotelRequest(state.searchBy)
    _setFiltersOpen(false)
  }

  const fetchHotelRequest = (resource, withFilters = false, currentState = null) => {
    let payload = preparePayloadForGetHotels(resource, (currentState || state), withFilters, 1)

    if (!withFilters) _setHotels([])
    getHotels(dispatch, payload, state.displaySearchMessage, withFilters)
    __getUser()
    if (isBookingAgent) {
      if (state.leadId) {
        __getZohoLead()
      } else {
        _setZohoLead(null)
      }
    }

    if (withFilters) {
      dispatch({type: SEARCH.FILTERS.LAST, payload: state.filters})
    }

    setMobileOpen(false)
  }

  const _setSearchByHandler = (opts) => {
    _setSearchBy(opts)
    setMoveToNextField(true)
  }

  const _toggleSearchBar = () => {
    setMobileOpen(!mobileOpen)

    if (!mobileOpen) _setFiltersOpen(false)
  }

  const _renderTimer = () => {
    if (state.hotels && state.hotels.length !== 0) {
      return (
        <Timer
          initialTime={0}
          checkpoints={[
            {
              time: 60000 * 21,
              callback: () => {
                _setNewState({ ...state, searchReloading: true })
                _submitSearch(new Event("searchReload"))
              },
            },
          ]}
        />
      )
    }
  }

  const _toggleAdvanced = (e) => {
    e.preventDefault()
    setAdvancedOpen(!advancedOpen)
  }

  const _toggleFilterResults = () => _setFiltersOpen(!state.filtersOpen)

  const _displaySearchDetails = () => {
    return (
      <p className="search-details">
        {state.searchBy.id !== null && (
          <span className="dest border-bottom">
            <i className="fas fa-globe-americas" />
            {state.searchBy.label}
          </span>
          )}
        {state.searchBy.id !== null && (
          <span className="dates-span border-bottom">
            <i className="far fa-calendar-alt" />
            {state.arrival && (
              <span className="arrival-span">
                {state.arrival.format("MMM Do")}
              </span>
            )}
            {departure && (
              <span className="departure-span"> to {departure.format("MMM Do")}</span>
            )}
          </span>
        )}
        {(state.searchBy.id !== null) && !isMobilyOnly && (
          <span className="tablet-details">
            {state.adults && (
              <span className="adults-span border-bottom">
                <i className="fas fa-user-alt" /> {state.adults} {
                state.adults == 1 ? 'Adult' : 'Adults'}
              </span>
            )}
            {state.rooms && (
              <span className="rooms-span border-bottom">
                <i className="fas fa-bed" /> {state.rooms} {
                state.rooms == 1 ? 'Room' : 'Rooms'}
              </span>
            )}
          </span>
        )}
      </p>
    )
  }

  return (
    <div
      className={`search-bar search disable-dbl-tap-zoom ${
        mobileOpen ? "open" : "closed"} ${
        state.hotels.length > 0 ? ' results' : ''}`}
      >
      <div className="fields">
        <SearchDestination
          searchByHandler={ _setSearchByHandler }
          />

        <SearchCalendar
          departure       = { departure }
          setDeparture    = { setDeparture }
          activateMe      = { moveToNextField }
          />

        <div className={`room-data-group ${
          state.rooms > 1 && roomsOpen
          ? 'room-panel-open'
          : state.rooms > 1 && !roomsOpen
          ? 'room-panel-closed'
          : ''
          }`}>
          <SearchAdults
            setAdults       = {_setAdults}
            error           = { errors.adults }
            controlledValue = { state.adults }
            optionsLength   = { maxAdults }
            disabled        = { state.rooms > 1 }
            />
          <SearchKids
            isBookingAgent  = { isBookingAgent }
            controlledValue = { state.children }
            optionsLength   = { maxKids }
            disabled        = { state.rooms > 1 }
            />
          <SearchRooms
            setMaxAdults = { setMaxAdults }
            setMaxKids   = { setMaxKids }
            />

          {roomsOpen && (
            <RoomsDataPanel
              open       = { roomsOpen }
              toggleOpen = { setRoomsOpen }
            />
          )}
          {
            state.rooms > 1 && !roomsOpen && (
              <button
                className = "text plain toggle-room-panel"
                role      = "button"
                onClick   = {() => setRoomsOpen(!roomsOpen)}
                >
                <i className="fas fa-chevron-down" />
              </button>
            )
          }
        </div>

        {shouldShowAdvanced(state)  && !advancedOpen && !isMobile && (
          <button
            className = "text plain advanced-search"
            role      = "button"
            onClick   = {_toggleAdvanced}
            >
            <span>{state.hotels.length > 0 ? "Modify" : "Advanced Search"}</span>
            <i className="fas fa-caret-down" />
          </button>
        )}
      </div>

      <div className="search-submit">
        <button
          type       = "submit"
          className  = {`button primary ${
            valid &&
            state.hotels.length == 0
            ? 'animated heartBeat infinite slower' : ''}`
          }
          disabled   = {!valid}
          aria-label = "Search Hotels"
          onClick    = {(e) => _submitSearch(e)}
        >
          <span>Search</span>
          <i className="fas fa-arrow-right" />
        </button>
      </div>

      <div className={`submit ${ mobileOpen ? "opened": "closed"}`}>
        <button
          className = {`toggle-search-bar-on-mobile text plain close ${mobileOpen ? "opened": "closed"}`}
          onClick   = {() => _toggleSearchBar()}
          title     = "Toggle search bar on smaller screens"
          aria-hidden = {!isMobile}
          aria-label  = "Toggle search bar on smaller screens"
          role        = "button"
        >
          <i className={`fas ${mobileOpen ? "fa-chevron-up" : "fa-search"}`} />
          { !mobileOpen && _displaySearchDetails() }
          { !mobileOpen && <i className="fas fa-chevron-down down" />}
        </button>
      </div>

      {_renderTimer()}

      { advancedOpen && !isMobile && (
        <AdvancedSearchPanel
          toggleOpen        = { setAdvancedOpen }
          open              = { advancedOpen }
          isBookingAgent    = { isBookingAgent }
          setIsBookingAgent = { setIsBookingAgent }
        />
      )}

      { state.filtersOpen && (
        <FilterResults
          open              = { state.filtersOpen }
          toggleOpen        = { _toggleFilterResults }
          />
        )
      }
    </div>
  )
}

export default SearchBar
